import { AfterViewInit, Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { Epe, EpeMongo, Vote } from 'app/models/epe';
import { AssociationsService } from 'app/services/associations.service';
import { ApplicationsService } from 'app/services/applications.service';
import { EpesService } from 'app/services/epes.service';
import { SopService } from 'app/services/sop.service';
import { Alert } from 'app/shared/alerts/simple-message/simple-message.component';
import { VoteOverride } from 'app/shared/sopcard/vote-breakdown/vote-breakdown.component';
import { SessionStorage } from 'app/storage/session.storage';
import { SopStorage } from 'app/storage/sop.storage';
import { Logger } from 'app/utilities/logger';
import { catchError, EMPTY, forkJoin, lastValueFrom, Observable } from 'rxjs';

@Component({
  selector: 'epe-sopcard-small',
  templateUrl: './epe-sopcard-small.component.html',
  styleUrls: ['./epe-sopcard-small.component.scss', '../sopcard.component.scss']
})
export class EpeSopcardSmallComponent implements OnInit, AfterViewInit {

  @Input() epe!: Epe
  @Output() voteUpdated: EventEmitter<Vote> = new EventEmitter<Vote>()

  vote: Vote = new Vote(false)
  votedByMe: boolean = false
  votedBySomeone: boolean = false

  standardisation?: number
  engagment?: number
  checked: boolean = false

  showAlert: boolean = false
  alertMessage: string = ''
  alertType: Alert = Alert.SUCCESS

  note = ''

  // ── helpers ────────────────────────────────────────────────────────────────

  /** MongoDB application _id string (new hsb2 flow) */
  private get appId(): string | null {
    const raw = this.epe._id
    if (!raw) return null
    return raw?.['$oid'] ?? String(raw)
  }

  /** True when this is a new Application object (not a legacy Drupal Epe) */
  private get isNewApp(): boolean {
    return !!this.appId
  }

  constructor(
    private drupalService: SopService,
    private mongoService: EpesService,
    private applicationsService: ApplicationsService,
    private cache: SopStorage,
    public auth: SessionStorage,
    private logger: Logger,
    private associationService: AssociationsService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.epe.sop_status = this.epe.sop_status || 'pending'
    if (this.epe.sop_status === 'pending') return

    this.getVotes()
    if (this.auth.isCallManager() || this.auth.isProjectBoard()) {
      this.getNote()
    }
  }

  // ── vote / note loading ────────────────────────────────────────────────────

  private getNote(): void {
    if (this.isNewApp) {
      this.applicationsService.getNote(this.appId!).subscribe(n => { this.note = n })
    } else {
      this.mongoService.getNote(this.epe.id).subscribe(n => {
        this.note = n
        this.cache.setNote(this.epe.id, n)
      })
    }
  }

  private getVotes(): void {
    if (this.isNewApp) {
      this.applicationsService.getVoteDetail(this.appId!).subscribe(vote => {
        this.vote = vote
        this.computeVoteRows()
        this.voteUpdated.emit(this.vote)
        if (vote?.voters?.indexOf(this.auth.getUsername()) !== -1) {
          this.votedByMe = true
          this.getVoteByMe()
        }
        this.updateEpeScore()
      })
    } else {
      this.mongoService.getVote(this.epe.id).subscribe(vote => {
        this.logger.info(vote, 'Vote:')
        this.vote = vote
        this.computeVoteRows()
        this.voteUpdated.emit(this.vote)
        this.cache.setVote(this.epe.id, this.vote)
        if (vote.voters?.indexOf(this.auth.getUsername()) !== -1) {
          this.votedByMe = true
        }
        if (vote.voters?.length) this.votedBySomeone = true
        this.getVoteByMe()
        this.updateEpeScore()
      })
    }
  }

  private getVoteByMe(): void {
    if (this.isNewApp) {
      this.applicationsService.getVoteDetail(this.appId!, this.auth.getUsername()).subscribe(v => {
        if (v?.standardisation != null && v?.engagment != null) {
          this.votedByMe = true
          this.standardisation = v.standardisation
          this.engagment = v.engagment
        }
      })
    } else {
      this.mongoService.getVote(this.epe.id, this.auth.getUsername()).subscribe(v => {
        if (v.standardisation != null && v.engagment != null) {
          this.votedByMe = true
          this.standardisation = this.getNumber(v.standardisation)
          this.engagment = this.getNumber(v.engagment)
        }
      })
    }
  }

  async updateEpeScore(): Promise<void> {
    if (this.isNewApp) {
      const scores = await lastValueFrom(this.applicationsService.getScore(this.appId!), { defaultValue: null })
      if (!scores) return
      this.epe.score = scores.score
      this.epe.nvotes = scores.nvotes
      this.epe.engVariance = scores.engVariance
      this.epe.standVariance = scores.standVariance
    } else {
      const scores = await lastValueFrom(this.mongoService.getScore(this.epe.id), { defaultValue: null })
      if (!scores) return
      this.epe.score = scores.score
      this.epe.nvotes = scores.nvotes
      this.epe.engVariance = scores.engVariance
      this.epe.standVariance = scores.standVariance
    }
  }

  // ── actions ────────────────────────────────────────────────────────────────

  change(): void {
    this.checked = !this.checked
  }

  submitNote(): void {
    const op: Observable<any> = this.isNewApp
      ? this.applicationsService.updateNote(this.appId!, this.note)
      : this.mongoService.updateNote(this.epe.id, this.note)

    op.pipe(catchError(() => {
      this.addAlert('Note submit failed! Please contact the administrator', Alert.ERROR)
      return EMPTY
    })).subscribe(() => {
      this.getNote()
      this.addAlert('Note saved!', Alert.SUCCESS)
    })
  }

  submitEligibility(): void {
    const sop_status = this.checked ? 'eligible' : 'not_eligible'

    if (this.isNewApp) {
      const userId: number = this.epe.user_id ?? this.epe.id
      this.applicationsService.updateStatus(this.appId!, sop_status as any, userId)
        .pipe(catchError(() => {
          this.addAlert('Eligibility check failed! Please contact the administrator', Alert.ERROR)
          return EMPTY
        }))
        .subscribe(() => {
          this.epe.sop_status = sop_status
          this.epe.status = sop_status
          this.cache.setSopStatus(this.epe.id, sop_status)
          this.addAlert('Eligibility check done.', Alert.SUCCESS)
          this.getVotes()
        })
    } else {
      // Legacy Drupal flow
      const epe = this.cache.getEpe(this.epe.id)
      const roles = epe?.standardisation_roles?.map((x: any) => x.role) ?? []
      const years = epe?.standardisation_roles?.map((x: any) => x.years) ?? []

      forkJoin({
        drupal: this.drupalService.setSopStatus('epe', this.epe.id, sop_status, this.epe.email, this.epe.name + ' ' + this.epe.surname),
        mongo: this.mongoService.createEpe(new EpeMongo(this.epe.id, sop_status, this.epe.gender === 'Female'), { roles, years })
      }).pipe(catchError(() => {
        this.addAlert('Eligibility check failed! Please contact the administrator', Alert.ERROR)
        return EMPTY
      })).subscribe(then => {
        this.cache.setSopStatus(this.epe.id, sop_status)
        this.cache.setEpeMongo(then.mongo)
        this.vote = then.mongo.vote
        this.addAlert('Eligibility check done. EPE can now be rated', Alert.SUCCESS)
      })
    }
  }

  submitVote(): void {
    if (this.standardisation == null || this.engagment == null) {
      this.addAlert('Please add votes before submitting', Alert.WARNING)
      return
    }
    if (this.standardisation < 0 || this.standardisation > 10 ||
        this.engagment < 0 || this.engagment > 10) {
      this.addAlert('Votes must be between 0 and 10', Alert.WARNING)
      return
    }

    const op: Observable<any> = this.isNewApp
      ? this.applicationsService.vote(this.appId!, this.standardisation, this.engagment, this.auth.getUsername())
      : this.mongoService.updateVote(this.standardisation, this.engagment, this.epe.id, this.auth.getUsername())

    op.pipe(catchError(() => {
      this.addAlert('Vote submit failed! Please contact the administrator', Alert.ERROR)
      return EMPTY
    })).subscribe(() => {
      this.getVotes()
      this.votedByMe = true
      this.votedBySomeone = true
      this.addAlert('Vote submitted!', Alert.SUCCESS)
    })
  }

overrideVote(change: VoteOverride): void {
    if (!this.isNewApp) {
      this.addAlert('Vote override is only available for applications submitted through the current form.', Alert.WARNING)
      return
    }
    this.applicationsService.overrideVote(this.appId!, change.voter, change.standardisation, change.engagement, this.auth.getUsername())
      .pipe(catchError(() => {
        this.addAlert('Vote override failed! Please contact the administrator', Alert.ERROR)
        return EMPTY
      }))
      .subscribe(() => {
        this.getVotes()
        this.addAlert(`Vote for ${change.voter} updated.`, Alert.SUCCESS)
      })
  }

  async stopVote(): Promise<void> {
    if (this.isNewApp) {
      const userId: number = this.epe.user_id ?? this.epe.id
      this.applicationsService.updateStatus(this.appId!, 'voted', userId)
        .pipe(catchError(() => {
          this.addAlert('Close voting failed! Please contact the administrator', Alert.ERROR)
          return EMPTY
        }))
        .subscribe(() => {
          this.epe.sop_status = 'voted'
          this.epe.status = 'voted'
          this.cache.setSopStatus(this.epe.id, 'voted')
          this.addAlert('Voting session closed.', Alert.SUCCESS)
        })
    } else {
      let projects = this.cache.getProjects()
      if (!projects) {
        // Legacy Drupal-only path. The fetched rows are raw Drupal projects
        // (Drupal id, no Mongo _id and therefore no application_id), so they are
        // used locally but deliberately NOT written into the shared SopStorage
        // cache, which the association-scoped pages read expecting application_id.
        projects = await lastValueFrom(this.drupalService.getAll('project'))
      }
      projects = projects.filter((x: any) => x.sop_status === 'eligible')

      forkJoin({
        drupal: this.drupalService.setSopStatus('epe', this.epe.id, 'voted'),
        association: this.associationService.createAssociation(projects, [this.epe])
      }).pipe(catchError(() => {
        this.addAlert('Vote stop failed! Please contact the administrator', Alert.ERROR)
        return EMPTY
      })).subscribe(() => {
        this.cache.setSopStatus(this.epe.id, 'voted')
        this.addAlert('Voting completed!', Alert.SUCCESS)
      })
    }
  }

  // ── utils ──────────────────────────────────────────────────────────────────

  truncateFloat(n: number): number {
    return Math.floor(n * 10) / 10
  }

  getNumber(x: number | number[]): number {
    return typeof x === 'number' ? x : -1
  }

  addAlert(message: string, type: Alert): void {
    this.alertMessage = message
    this.alertType = type
    this.showAlert = true
  }

  isWarning(): boolean {
    if (!this.epe.engVariance || !this.epe.standVariance) return false
    return this.epe.engVariance >= 2 || this.epe.standVariance >= 2
  }

  get avgEngagement(): number {
    const arr = Array.isArray(this.vote.engagment) ? this.vote.engagment as number[] : []
    if (!arr.length) return 0
    return this.truncateFloat(arr.reduce((s, v) => s + v, 0) / arr.length)
  }

  get avgStandardisation(): number {
    const arr = Array.isArray(this.vote.standardisation) ? this.vote.standardisation as number[] : []
    if (!arr.length) return 0
    return this.truncateFloat(arr.reduce((s, v) => s + v, 0) / arr.length)
  }

  /**
   * Stable field (not a getter): re-computed only when `vote` actually changes.
   * Bound as [rows]="voteRows" in the template, which Angular re-reads on every
   * change-detection cycle — a getter here would return new array/object
   * identities on every tick, forcing vote-breakdown's *ngFor to tear down and
   * rebuild every row (including the edit button) on any click/keystroke
   * anywhere on the page.
   */
  voteRows: { voter: string; automatic: number; engagement: number; standardisation: number; gender: number; edited_by?: string | null; edited_at?: string | null }[] = []

  private computeVoteRows(): void {
    const voters = this.vote.voters ?? []
    const eng    = Array.isArray(this.vote.engagment)       ? this.vote.engagment       as number[] : []
    const stand  = Array.isArray(this.vote.standardisation) ? this.vote.standardisation as number[] : []
    const editedBy = this.vote.edited_by ?? []
    const editedAt = this.vote.edited_at ?? []
    const auto   = this.vote.automatic ?? 0
    const gender = this.vote.gender    ?? 0
    this.voteRows = voters.map((v, i) => ({
      voter: v,
      automatic: auto,
      engagement: eng[i] ?? 0,
      standardisation: stand[i] ?? 0,
      gender,
      edited_by: editedBy[i] ?? null,
      edited_at: editedAt[i] ?? null,
    }))
  }
}
